import { Button, Card, Space, Typography } from '@pankod/refine-antd'
import { useCallback, useEffect, useRef, useState } from 'react'
import { supabaseClient } from 'utility'

type State = 'not-started' | 'started' | 'finished' | 'review'
type Action = 'status' | 'start' | 'finish'
const validStates: State[] = ['not-started', 'started', 'finished', 'review']

export const SimpleJornada: React.FC = () => {
  const [state, setState] = useState<State | null>(null)
  const [error, setError] = useState(false)
  const [busy, setBusy] = useState(false)
  const [serverTime, setServerTime] = useState<string | null>(null)
  const [startedAt, setStartedAt] = useState<string | null>(null)
  const [endedAt, setEndedAt] = useState<string | null>(null)
  const pending = useRef(false)
  const mounted = useRef(true)

  const request = useCallback(async (action: Action) => {
    if (pending.current) return
    pending.current = true
    setBusy(true)
    setError(false)
    try {
      const { data, error: requestError } = await supabaseClient.rpc('employee_jornada', { action })
      const result = data as { state?: State; startedAt?: string | null; endedAt?: string | null; serverTime?: string } | null
      if (requestError || !result?.state || !validStates.includes(result.state)) throw new Error('Unable to load attendance state')
      if (mounted.current) {
        setState(result.state)
        setServerTime(result.serverTime || null)
        setStartedAt(result.startedAt || null)
        setEndedAt(result.endedAt || null)
      }
    } catch {
      // Never indicate success on a network error; reload authoritative state.
      if (mounted.current) { setError(true); setState(null) }
    } finally {
      pending.current = false
      if (mounted.current) setBusy(false)
    }
  }, [])

  useEffect(() => {
    mounted.current = true
    request('status')
    const refresh = () => { if (document.visibilityState === 'visible') request('status') }
    document.addEventListener('visibilitychange', refresh)
    const interval = window.setInterval(refresh, 60000)
    return () => {
      mounted.current = false
      document.removeEventListener('visibilitychange', refresh)
      window.clearInterval(interval)
    }
  }, [request])

  return <Card title="Registro de jornada" style={{ maxWidth: 560 }}>
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {serverTime && <Typography.Text>{new Intl.DateTimeFormat('es-ES', { timeZone: 'Europe/Madrid', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(serverTime))}</Typography.Text>}
      {startedAt && <Typography.Text>Hora de inicio: {new Intl.DateTimeFormat('es-ES', { timeZone: 'Europe/Madrid', hour: '2-digit', minute: '2-digit' }).format(new Date(startedAt))}</Typography.Text>}
      {endedAt && <Typography.Text>Hora de finalización: {new Intl.DateTimeFormat('es-ES', { timeZone: 'Europe/Madrid', hour: '2-digit', minute: '2-digit' }).format(new Date(endedAt))}</Typography.Text>}
      {error && <Typography.Text type="danger">No se ha podido comprobar el registro. Vuelve a intentarlo.</Typography.Text>}
      {state === 'started' && <Typography.Text>Jornada iniciada</Typography.Text>}
      {state === 'finished' && <Typography.Text>Jornada finalizada</Typography.Text>}
      {state === 'review' && <Typography.Text type="danger">Hay registros de hoy que necesitan revisión. Contacta con administración.</Typography.Text>}
      <Space wrap>
        <Button type="primary" size="large" disabled={busy || state !== 'not-started'} onClick={() => request('start')}>Iniciar jornada</Button>
        <Button size="large" disabled={busy || state !== 'started'} onClick={() => request('finish')}>Finalizar jornada</Button>
        {error && <Button onClick={() => request('status')}>Reintentar</Button>}
      </Space>
    </Space>
  </Card>
}
